export type NormalizedOverview = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  compensation: {
    type: 'HOURLY' | 'SALARY';
    hourlyRate: number | null;
    monthlySalaryEUR: number | null;
    currency: string;
    valid_from?: string | null;
  };
  working_pattern: {
    hours_per_week: number;
    days_distribution: number[];
  };
  leave_balance: {
    available: number;
    total: number;
    used: number;
    approved: number;
    requested: number;
    source?: string;
    contractual_per_year?: number;
    entitlement_current_year?: number;
    statutory_days?: number;
    extra_days?: number;
    prorata_months?: number;
    is_explicit?: boolean;
  };
  absences: {
    requested: number;
    approved: number;
    rejected: number;
  };
  upcoming_absences: any[];
  balances?: {
    month_current_h?: number;
    month_prev_h?: number;
    total_balance_h?: number;
    delta_prev_h?: number;
  };
  next_shifts: any[];
  last_time_trackings: any[];
  key_facts: {
    employment_type?: string;
    contract_end_date?: string;
    weekly_hours: number;
  };
  onboarding_status?: string;
  deactivated_at?: string;
};

const toNum = (v: unknown, def = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

export function normalizeOverview(raw: any): NormalizedOverview {
  const emp = raw?.employee ?? raw;
  const comp = raw?.compensation ?? {};
  const type: 'HOURLY' | 'SALARY' = comp?.compensation_type === 'SALARY' ? 'SALARY' : 'HOURLY';

  const hourlyRate = type === 'HOURLY' ? toNum(comp?.hourly_rate, 0) : null;
  const monthlySalaryEUR =
    type === 'SALARY' ? toNum((comp?.salary_monthly_cents ?? 0) / 100, 0) : null;

  const distRaw = raw?.working_pattern?.days_distribution;
  const days_distribution = Array.from({ length: 7 }, (_, i) => toNum(distRaw?.[i], 0));

  const leaveBalance = raw?.leave_balance ?? {};
  const absenceCounts = raw?.absence_counts ?? raw?.absences ?? {};
  const keyFacts = raw?.key_facts ?? raw?.facts ?? {};
  const balances = raw?.balances;

  const normalizedTTs = (raw?.last_time_trackings ?? []).map((tt: any) => ({
    ...tt,
    hours: toNum(tt?.hours, 0)
  }));

  return {
    id: emp?.id ?? raw?.id ?? 0,
    first_name: emp?.first_name ?? raw?.first_name ?? '',
    last_name: emp?.last_name ?? raw?.last_name ?? '',
    email: emp?.email ?? raw?.email ?? '',
    compensation: {
      type,
      hourlyRate,
      monthlySalaryEUR,
      currency: comp?.currency || 'EUR',
      valid_from: comp?.valid_from ?? null,
    },
    working_pattern: {
      hours_per_week: toNum(raw?.working_pattern?.hours_per_week ?? keyFacts?.weekly_hours, 0),
      days_distribution,
    },
    leave_balance: {
      available: toNum(leaveBalance?.available, 0),
      total: toNum(leaveBalance?.total, 0),
      used: toNum(leaveBalance?.used, 0),
      approved: toNum(leaveBalance?.approved, 0),
      requested: toNum(leaveBalance?.requested, 0),
      source: leaveBalance?.source,
      contractual_per_year: leaveBalance?.contractual_per_year,
      entitlement_current_year: leaveBalance?.entitlement_current_year,
      statutory_days: leaveBalance?.statutory_days,
      extra_days: leaveBalance?.extra_days,
      prorata_months: leaveBalance?.prorata_months,
      is_explicit: leaveBalance?.is_explicit,
    },
    absences: {
      requested: toNum(absenceCounts?.requested, 0),
      approved: toNum(absenceCounts?.approved, 0),
      rejected: toNum(absenceCounts?.rejected, 0),
    },
    upcoming_absences: Array.isArray(raw?.upcoming_absences) ? raw.upcoming_absences : [],
    balances: balances ? {
      month_current_h: balances.month_current_h,
      month_prev_h: balances.month_prev_h,
      total_balance_h: balances.total_balance_h,
      delta_prev_h: balances.delta_prev_h,
    } : undefined,
    next_shifts: Array.isArray(raw?.next_shifts) ? raw.next_shifts : [],
    last_time_trackings: normalizedTTs,
    key_facts: {
      employment_type: keyFacts?.employment_type,
      contract_end_date: keyFacts?.contract_end_date,
      weekly_hours: toNum(keyFacts?.weekly_hours, 0),
    },
    onboarding_status: emp?.onboarding_status ?? raw?.onboarding_status,
    deactivated_at: emp?.deactivated_at ?? raw?.deactivated_at,
  };
}

export const formatCurrencyEUR = (amount: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

export const formatNum = (n: number, digits = 0): string =>
  Number.isFinite(n) ? n.toFixed(digits) : '—';
